import { ReactNode } from 'react';
import { ModuleHeader } from '../ui/ModuleHeader';
import { LucideIcon } from 'lucide-react';
import { useHubShellEmbedded } from './HubShellContext';

interface PageLayoutProps {
  title: string;
  description: string;
  icon?: LucideIcon;
  actions?: ReactNode;
  children: ReactNode;
  /** When true, render only children (no page chrome). Auto-detected inside HubShell. */
  embedded?: boolean;
}

/**
 * PageLayout Component
 *
 * A consistent layout wrapper for all sidebar pages that provides:
 * - Standard padding and max-width constraints
 * - ModuleHeader with consistent styling
 * - Flexible content area
 *
 * Inside HubShell tabs, nested PageLayouts automatically collapse to content-only
 * so headers and scroll containers are not duplicated.
 */
export function PageLayout({
  title,
  description,
  icon,
  actions,
  children,
  embedded: embeddedProp,
}: PageLayoutProps) {
  const embeddedContext = useHubShellEmbedded();
  const embedded = embeddedProp ?? embeddedContext;

  if (embedded) {
    return <div className="space-y-6">{children}</div>;
  }

  return (
    <div className="min-h-screen p-6 lg:p-8 bg-khiimori-bg-primary">
      <div className="max-w-7xl mx-auto space-y-6">
        <ModuleHeader
          title={title}
          description={description}
          icon={icon}
          actions={actions}
        />
        {children}
      </div>
    </div>
  );
}
